import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader, Card } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Search, Hash, Landmark, Trash2, Plus, Info, CheckCircle2, AlertTriangle, FileText, List, PhoneCall, FileSearch, PenTool, Tags, XOctagon, IndianRupee, ShieldAlert, KeyRound, Megaphone, Server, Sparkles, FolderOpen, ChevronRight, BookMarked, Settings } from "lucide-react";
import { FORM_GUIDES, INTAKE_DROPDOWNS } from "@/lib/referenceData";

const EMPTY = { code: "", category: "", category_hi: "", en: "", hi: "", desc_en: "", desc_hi: "", txn_format: "", portal: "NCRP – Other Cyber Crime", cat_desc_en: "", cat_desc_hi: "" };

const BUCKET_STYLE = {
  "Financial Fraud": { icon: IndianRupee, accent: "#059669", border: "border-emerald-500", bg: "bg-emerald-50", text: "text-emerald-600" },
  "Harassment & Sexual Exploitation": { icon: ShieldAlert, accent: "#DC2626", border: "border-red-500", bg: "bg-red-50", text: "text-red-600" },
  "Hacking & Account Compromise": { icon: KeyRound, accent: "#7C3AED", border: "border-purple-500", bg: "bg-purple-50", text: "text-purple-600" },
  "Fake News & Hoax": { icon: Megaphone, accent: "#D97706", border: "border-amber-500", bg: "bg-amber-50", text: "text-amber-600" },
  "Cyber Attack on Systems": { icon: Server, accent: "#2563EB", border: "border-blue-500", bg: "bg-blue-50", text: "text-blue-600" },
  "Emerging Scams": { icon: Sparkles, accent: "#DB2777", border: "border-pink-500", bg: "bg-pink-50", text: "text-pink-600" },
  "Other Cyber Crimes": { icon: FolderOpen, accent: "#475569", border: "border-slate-400", bg: "bg-slate-100", text: "text-slate-600" },
};
const getBucketStyle = (cat) => BUCKET_STYLE[cat] || BUCKET_STYLE["Other Cyber Crimes"];

export default function Reference() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const isAdmin = user?.role === "admin";
  const [tax, setTax] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedFormGuide, setSelectedFormGuide] = useState(null);
  const [activeTab, setActiveTab] = useState("");

  const load = () => api.get("/taxonomy").then((r) => setTax(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const add = async () => {
    setSaving(true);
    try {
      await api.post("/admin/taxonomy", form);
      toast.success("Category added");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (code) => {
    try { await api.delete(`/admin/taxonomy/${code}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const filtered = tax.filter((c) =>
    [c.en, c.hi, c.category, c.category_hi, c.code].some((f) => (f || "").toLowerCase().includes(q.toLowerCase()))
  );
  const groups = filtered.reduce((acc, c) => { (acc[c.category] = acc[c.category] || []).push(c); return acc; }, {});
  const categories = Array.from(new Set(tax.map((c) => c.category)));
  const groupKeys = Object.keys(groups);
  const currentTab = groupKeys.includes(activeTab) ? activeTab : (groupKeys[0] || "");

  return (
    <div>
      <PageHeader title={t("reference")} subtitle="NCRP crime classification · describe-what-happened taxonomy"
        action={isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F172A] hover:bg-slate-800" data-testid="add-taxonomy"><Plus className="h-4 w-4 mr-1.5" />Add Category</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Add Reference Category</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Code</Label><Input value={form.code} onChange={(e) => set("code", e.target.value)} placeholder="e.g. FIN99" data-testid="tx-code" className="mt-1" /></div>
                  <div><Label>Bucket</Label>
                    <Input list="cats" value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="Financial Fraud" data-testid="tx-category" className="mt-1" />
                    <datalist id="cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
                  </div>
                </div>
                <div><Label>Bucket (HI)</Label><Input value={form.category_hi} onChange={(e) => set("category_hi", e.target.value)} className="mt-1" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name (EN)</Label><Input value={form.en} onChange={(e) => set("en", e.target.value)} data-testid="tx-en" className="mt-1" /></div>
                  <div><Label>Name (HI)</Label><Input value={form.hi} onChange={(e) => set("hi", e.target.value)} data-testid="tx-hi" className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Description (EN)</Label><Input value={form.desc_en} onChange={(e) => set("desc_en", e.target.value)} className="mt-1" /></div>
                  <div><Label>Description (HI)</Label><Input value={form.desc_hi} onChange={(e) => set("desc_hi", e.target.value)} className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Txn ID Format</Label><Input value={form.txn_format} onChange={(e) => set("txn_format", e.target.value)} className="mt-1" /></div>
                  <div><Label>Portal Section</Label><Input value={form.portal} onChange={(e) => set("portal", e.target.value)} className="mt-1" /></div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={add} disabled={saving || !form.code || !form.category || !form.en} className="bg-[#0F172A] hover:bg-slate-800" data-testid="tx-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )} />

      <Tabs defaultValue="forms" onValueChange={setActiveTab}>
        <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center mb-6">
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger value="forms" className="data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-sm">Forms & Scenarios Guides</TabsTrigger>
            <TabsTrigger value="taxonomy" className="data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-sm">Category Taxonomy</TabsTrigger>
            <TabsTrigger value="dropdowns" className="data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-sm">Intake Dropdowns</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="taxonomy">
          <div className="relative max-w-md mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("searchTaxonomy")} className="pl-9" data-testid="reference-search" />
          </div>

      {groupKeys.length > 0 ? (
        <Tabs value={currentTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto pb-2 mb-4">
            <TabsList className="inline-flex w-max">
              {groupKeys.map((cat) => (
                <TabsTrigger key={cat} value={cat}>
                  {lang === "en" ? cat : (groups[cat][0]?.category_hi || cat)}
                  <span className="ml-2 text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">{groups[cat].length}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {groupKeys.map((cat) => {
            const style = getBucketStyle(cat);
            const BucketIcon = style.icon;
            return (
            <TabsContent key={cat} value={cat} className="space-y-4">
              {(groups[cat][0].cat_desc_en || groups[cat][0].cat_desc_hi) && (
                <div className={`flex items-start gap-3 ${style.bg} border border-slate-200 rounded-lg p-4 max-w-3xl mb-4`}>
                  <BucketIcon className={`h-5 w-5 shrink-0 mt-0.5 ${style.text}`} />
                  <p className="text-sm text-slate-700 leading-relaxed">{lang === "en" ? groups[cat][0].cat_desc_en : groups[cat][0].cat_desc_hi}</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {groups[cat].map((c) => (
                  <Card
                    key={c.code}
                    data-testid={`ref-${c.code}`}
                    className={`relative cursor-pointer hover:shadow-lg transition-shadow border-l-4 ${style.border} group`}
                    onClick={() => setSelectedCard(c)}
                  >
                    {isAdmin && (
                      <button onClick={(e) => { e.stopPropagation(); del(c.code); }} data-testid={`delete-ref-${c.code}`}
                        className="absolute top-3 right-3 text-slate-300 hover:text-red-600 transition-colors z-10"><Trash2 className="h-4 w-4" /></button>
                    )}
                    <div className="flex items-start gap-2 mb-2 pr-6">
                      <div className={`shrink-0 h-8 w-8 rounded-md ${style.bg} flex items-center justify-center`}>
                        <BucketIcon className={`h-4 w-4 ${style.text}`} />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-slate-900 leading-tight">{lang === "en" ? c.en : c.hi}</h4>
                        <span className="text-[10px] font-mono text-slate-400">{c.code}</span>
                      </div>
                    </div>
                    {(c.desc_en || c.desc_hi) && (
                      <p className="text-sm text-slate-600 mb-3 line-clamp-2">{lang === "en" ? c.desc_en : c.desc_hi}</p>
                    )}
                    <div className="flex flex-col gap-1.5 text-xs border-t border-slate-100 pt-2.5">
                      {c.txn_format && <div className="flex items-center gap-1.5 text-slate-500"><Hash className="h-3.5 w-3.5" /> {t("txnFormat")}: <span className="text-slate-700 font-medium">{c.txn_format}</span></div>}
                      {c.portal && <div className="flex items-center gap-1.5 text-slate-500"><Landmark className="h-3.5 w-3.5" /> {t("portal")}: <span className="text-slate-700 font-medium">{c.portal}</span></div>}
                    </div>
                    <div className={`flex items-center gap-1 mt-3 text-xs font-semibold ${style.text} opacity-0 group-hover:opacity-100 transition-opacity`}>
                      View full details <ChevronRight className="h-3.5 w-3.5" />
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>
            );
          })}
        </Tabs>
      ) : (
        <div className="text-slate-500 text-center py-12">No categories found.</div>
      )}
      </TabsContent>
      <TabsContent value="forms" className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {FORM_GUIDES.map((guide) => (
            <Card key={guide.id} className="border-l-4 relative cursor-pointer hover:shadow-lg transition-shadow border-indigo-500" onClick={() => setSelectedFormGuide(guide)}>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-5 w-5 text-indigo-600" />
                  <h3 className="font-head font-bold text-slate-900">{guide.title}</h3>
                </div>
                <div className="mb-3">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase mb-1">
                    <PhoneCall className="h-3.5 w-3.5" /> Victim Context
                  </div>
                  <p className="text-[13px] text-slate-700 leading-relaxed line-clamp-3">{guide.context}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </TabsContent>

      <TabsContent value="dropdowns" className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {INTAKE_DROPDOWNS.map((cat, idx) => (
            <Card key={idx} className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <List className="h-5 w-5 text-emerald-600" />
                <h3 className="text-base font-bold text-slate-900">{cat.category}</h3>
              </div>
              <ul className="space-y-3">
                {cat.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                    <div>
                      <span className="text-sm font-medium text-slate-800">{item.label}</span>
                      {item.desc && <span className="block text-xs text-slate-500 mt-0.5">{item.desc}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      </TabsContent>
      </Tabs>

      <Dialog open={!!selectedCard} onOpenChange={(open) => !open && setSelectedCard(null)}>
        <DialogContent className="max-w-xl p-0 overflow-hidden border-0 bg-transparent shadow-2xl">
          {selectedCard && (() => {
            const style = getBucketStyle(selectedCard.category);
            const BucketIcon = style.icon;
            return (
              <>
                <div className="p-6 text-white relative" style={{ backgroundColor: style.accent }}>
                  <div className="flex items-center gap-2 text-white/80 mb-2 text-sm font-semibold uppercase tracking-wider">
                    <BucketIcon className="h-4 w-4" /> {lang === "en" ? selectedCard.category : selectedCard.category_hi}
                  </div>
                  <DialogTitle className="text-2xl font-bold text-white">
                    {lang === "en" ? selectedCard.en : selectedCard.hi}
                  </DialogTitle>
                </div>

                <div className="p-6 space-y-5 bg-slate-50 overflow-y-auto max-h-[70vh]">
                  {(selectedCard.desc_en || selectedCard.desc_hi) && (
                    <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                      <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">
                        <Info className="h-4 w-4 text-blue-500" /> What This Covers
                      </div>
                      <p className="text-[15px] text-slate-800 leading-relaxed">{lang === "en" ? selectedCard.desc_en : selectedCard.desc_hi}</p>
                    </div>
                  )}

                  <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">
                      <BookMarked className="h-4 w-4 text-indigo-500" /> Reference Details
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex flex-col p-3 border border-slate-100 rounded-md bg-slate-50">
                        <span className="text-xs text-slate-500">Code</span>
                        <span className="font-mono text-sm font-semibold text-slate-900">{selectedCard.code}</span>
                      </div>
                      <div className="flex flex-col p-3 border border-slate-100 rounded-md bg-slate-50">
                        <span className="text-xs text-slate-500">Category Bucket</span>
                        <span className="text-sm font-semibold text-slate-900">{lang === "en" ? selectedCard.category : selectedCard.category_hi}</span>
                      </div>
                      {selectedCard.txn_format && (
                        <div className="flex flex-col p-3 border border-slate-100 rounded-md bg-slate-50">
                          <span className="text-xs text-slate-500">{t("txnFormat")}</span>
                          <span className="text-sm font-semibold text-slate-900">{selectedCard.txn_format}</span>
                        </div>
                      )}
                      {selectedCard.portal && (
                        <div className="flex flex-col p-3 border border-slate-100 rounded-md bg-slate-50">
                          <span className="text-xs text-slate-500">{t("portal")}</span>
                          <span className="text-sm font-semibold text-slate-900">{selectedCard.portal}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {(() => {
                    const procedure = lang === "en" ? selectedCard.procedure_en : selectedCard.procedure_hi;
                    return procedure && procedure.length > 0 && (
                      <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                        <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest mb-4">
                          <Settings className="h-4 w-4 text-blue-500" /> What The Call Taker Should Do
                        </div>
                        <ul className="space-y-3">
                          {procedure.map((step, i) => (
                            <li key={i} className="flex items-start gap-3 text-[15px] text-slate-700">
                              <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                              {step}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(selectedCard.escalation_en || selectedCard.escalation_hi) && (
                      <div className="bg-orange-50 p-5 rounded-lg border border-orange-200 shadow-sm">
                        <div className="flex items-center gap-2 text-sm font-bold text-orange-600 uppercase tracking-widest mb-3">
                          <AlertTriangle className="h-4 w-4" /> Escalation Trigger
                        </div>
                        <p className="text-[14px] text-orange-900 leading-relaxed font-medium">
                          {lang === "en" ? selectedCard.escalation_en : selectedCard.escalation_hi}
                        </p>
                      </div>
                    )}
                    {(selectedCard.prohibited_en || selectedCard.prohibited_hi) && (
                      <div className="bg-red-50 p-5 rounded-lg border border-red-200 shadow-sm">
                        <div className="flex items-center gap-2 text-sm font-bold text-red-600 uppercase tracking-widest mb-3">
                          <XOctagon className="h-4 w-4" /> Strictly Prohibited
                        </div>
                        <p className="text-[14px] text-red-900 leading-relaxed font-medium">
                          {lang === "en" ? selectedCard.prohibited_en : selectedCard.prohibited_hi}
                        </p>
                      </div>
                    )}
                  </div>

                  {(selectedCard.cat_desc_en || selectedCard.cat_desc_hi) && (
                    <div className="bg-amber-50 p-5 rounded-lg border border-amber-200 shadow-sm">
                      <div className="flex items-center gap-2 text-sm font-bold text-amber-600 uppercase tracking-widest mb-3">
                        <Info className="h-4 w-4" /> When To Use This Bucket
                      </div>
                      <p className="text-[14px] text-amber-900 leading-relaxed font-medium">
                        {lang === "en" ? selectedCard.cat_desc_en : selectedCard.cat_desc_hi}
                      </p>
                    </div>
                  )}
                </div>

                <div className="p-4 bg-white border-t border-slate-100 flex justify-end">
                  <Button variant="outline" onClick={() => setSelectedCard(null)}>Close</Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedFormGuide} onOpenChange={(open) => !open && setSelectedFormGuide(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden border-0 bg-transparent shadow-2xl">
          {selectedFormGuide && (
            <>
              <div className="bg-indigo-600 p-6 text-white relative">
                <div className="flex items-center gap-2 text-white/80 mb-2 text-sm font-semibold uppercase tracking-wider">
                  <FileText className="h-4 w-4" /> Forms & Scenarios Guide
                </div>
                <DialogTitle className="text-2xl font-bold text-white">
                  {selectedFormGuide.title}
                </DialogTitle>
              </div>
              
              <div className="p-6 space-y-5 bg-slate-50 overflow-y-auto max-h-[70vh]">
                <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">
                    <PhoneCall className="h-4 w-4 text-blue-500" /> Victim Context
                  </div>
                  <p className="text-[15px] text-slate-800 leading-relaxed">{selectedFormGuide.context}</p>
                </div>

                <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">
                    <FileSearch className="h-4 w-4 text-amber-500" /> Key Evidence to Extract
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedFormGuide.evidence.map((f, i) => (
                      <div key={i} className="flex flex-col p-3 border border-slate-100 rounded-md bg-slate-50 shadow-sm">
                        <span className="font-semibold text-sm text-slate-800">{f.field}</span>
                        <span className="text-xs text-slate-500 mt-1">{f.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">
                    <PenTool className="h-4 w-4 text-indigo-500" /> Form Entry Mapping
                  </div>
                  <p className="text-[15px] text-slate-800 leading-relaxed">{selectedFormGuide.mapping}</p>
                </div>

                <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">
                    <Tags className="h-4 w-4 text-emerald-500" /> Categorization Rules
                  </div>
                  <p className="text-[15px] text-slate-800 leading-relaxed">{selectedFormGuide.taxonomy}</p>
                </div>

                <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">
                    <XOctagon className="h-4 w-4 text-red-500" /> Fatal Entry Errors
                  </div>
                  <div className="p-4 rounded bg-red-50 border border-red-100 text-red-900 font-medium text-[15px] leading-relaxed">
                    {selectedFormGuide.fatal_error}
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-white border-t border-slate-100 flex justify-end">
                <Button variant="outline" onClick={() => setSelectedFormGuide(null)}>Close Guide</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
